import React, { Fragment, useState, useEffect, useRef } from "react";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Button } from "reactstrap";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import Alerts from "../../../components/Alerts/alerts";
import OrderService from "../order.service.js";
import AuthService from "../../Authentication/auth.service";
import Header from './Header';

const Orders = () => {
  const [branch, setBranch] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [pastOrdersCount, setPastOrdersCount] = useState(0);
  const [activeClicked, setActiveClicked] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  const fetchOrders = (active_orders, past_orders, page) => {
    const userString = localStorage.getItem('user');
    const user = JSON.parse(userString);
    const user_id = user['_id'];

    setLoading(true);
    OrderService.getAllOrders(active_orders, past_orders, page, user_id).then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else {
        if (page === 0) {
          setOrders(response.data.orderData);
        } else {
          setOrders(prevOrders => [...prevOrders, ...response.data.orderData]);
        }
        setActiveOrdersCount(response.data.activeOrdersCount);
        setPastOrdersCount(response.data.pastOrdersCount);
        if (response.data.orderData.length === 0) {
          setHasMore(false); // No more orders to load
        }
      }
    }).catch((error) => {
      setLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      if (errContent) {
        Alerts.swalErrorAlert(errContent);
      } else {
        Alerts.swalErrorAlert(t("Server Error, Please try again later."));
      }
    });
  };

  useEffect(() => {
    setOrders([]);
    setPage(0);
    setHasMore(true);
    fetchOrders(activeClicked, !activeClicked, 0); // Load orders based on activeClicked state
  }, [activeClicked]);

  useEffect(() => {
    if (page > 0) {
      fetchOrders(activeClicked, !activeClicked, page);
    }
  }, [page]);

  useEffect(() => {
    const observerInstance = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    }, { threshold: 1 });

    const lastCard = document.querySelector('.order-card:last-child');
    if (lastCard) {
      observerInstance.observe(lastCard);
    }

    return () => {
      if (lastCard) {
        observerInstance.unobserve(lastCard);
      }
    };
  }, [orders, hasMore]);

  const handleActiveOrdersClick = () => {
    if (!activeClicked) {
      setActiveClicked(true);
    }
  };

  const handlePastOrdersClick = () => {
    if (activeClicked) {
      setActiveClicked(false);
    }
  };

  const HandleOrderDetails = (order) => {
    navigate('/orders_details', {
      state: order
    });
  };

  const cardStyle = {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto 15px',
    borderRadius: '10px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    backgroundColor: '#fff',
  };

  const cardBodyStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '15px',
  };

  const orderInfoStyle = {
    flex: 1,
  };

  const orderIdStyle = {
    margin: '0 0 5px 0',
    fontSize: '18px',
    fontWeight: 'bold',
  };

  const orderDetailStyle = {
    margin: '0 0 3px 0',
    fontSize: '14px',
    color: '#6c757d',
  };

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <TransitionGroup>
            <CSSTransition component="div" classNames="TabsAnimation"
              appear={true} timeout={0} enter={false} exit={false}>
              <div>
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <Header branch={branch} setBranch={setBranch} />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <button
                      style={{
                        backgroundColor: activeClicked ? '#dc3545' : '#f8f9fa',
                        color: activeClicked ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '10px 20px',
                        marginRight: '10px',
                        cursor: 'pointer',
                      }}
                      onClick={handleActiveOrdersClick}
                    >
                      {activeOrdersCount} ACTIVE ORDERS
                    </button>
                    <button
                      style={{
                        backgroundColor: !activeClicked ? '#dc3545' : '#f8f9fa',
                        color: !activeClicked ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '10px 20px',
                        marginLeft: '10px',
                        cursor: 'pointer',
                      }}
                      onClick={handlePastOrdersClick}
                    >
                      {pastOrdersCount} PAST ORDERS
                    </button>
                  </div>
                  <div>
                    <LoadingOverlay tag="div" active={loading}
                      styles={{ overlay: (base) => ({ ...base }) }}
                      spinner={<DNALoader />}>
                      {orders.map((order, index) => {
                        const statusButtonStyle = {
                          backgroundColor:
                            order.status === 'confirmed'
                              ? '#ffc107' // Yellow
                              : order.status === 'processing'
                              ? '#007bff' // Blue
                              : order.status === 'in_transit'
                              ? '#6f42c1' // Purple/Orange
                              : order.status === 'cancelled'
                              ? '#dc3545' // Red
                              : 'green', // Default (Gray)
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          padding: '5px 10px',
                          fontSize: '14px',
                          marginRight: '15px',
                        };
                        const formattedStatus = order.status
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                        return (
                          <div
                            onClick={() => HandleOrderDetails(order)}
                            key={order.order_id}
                            style={cardStyle}
                            className="order-card"
                          >
                            <div style={cardBodyStyle}>
                              <Button style={statusButtonStyle}>{formattedStatus}</Button>
                              <div style={orderInfoStyle}>
                                <h5 style={orderIdStyle}>ID#{order.order_number}</h5>
                                <p style={orderDetailStyle}>
                                  &#128722; {order.total_cart_item} items
                                </p>
                                <p style={orderDetailStyle}>
                                  &#128337; {moment(order.order_date).format('MMM DD, YYYY - HH:mm')}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    </LoadingOverlay>
                  </div>
                </div>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    </Fragment>
  );
};

export default Orders;